import React, { useEffect, useMemo, useState } from 'react';
import {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';

import {
    Loader2,
    Bell,
    BellRing,
    Filter as FilterIcon,
    Search as SearchIcon,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
    CheckCircle2,
    XCircle,
    Clock,
    Sparkles,
} from 'lucide-react';

import { clientNotificationsAPI } from '@/services/api';

const fa = new Intl.NumberFormat('fa-IR');
const faDateTime = (d?: string | number | Date | null) =>
    d ? new Date(d).toLocaleString('fa-IR') : '—';

type ClientNotification = {
    id: number;
    title?: string | null;
    body?: string | null;
    message?: string | null;
    category?: string | null;
    type?: string | null;
    is_read?: boolean;
    created_at?: string;
    link_url?: string | null;
};

type ListResponse = {
    success?: boolean;
    items: ClientNotification[];
    total: number;
    page: number;
    limit: number;
};

const markMatch = (text: string, q: string) => {
    if (!q) return text;
    try {
        const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
        return (
            <>
                {parts.map((p, i) =>
                    p.toLowerCase() === q.toLowerCase() ? (
                        <mark key={i} className="bg-yellow-200 px-0.5 rounded">
                            {p}
                        </mark>
                    ) : (
                        <span key={i}>{p}</span>
                    ),
                )}
            </>
        );
    } catch {
        return text;
    }
};

const statusColor = (is_read?: boolean) =>
    is_read
        ? 'text-gray-600 bg-gray-50 border-gray-200'
        : 'text-emerald-800 bg-emerald-50 border-emerald-200';

const statusLabel = (is_read?: boolean) => (is_read ? 'خوانده شده' : 'خوانده نشده');

const NotificationsPage: React.FC = () => {
    const [items, setItems] = useState<ClientNotification[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');

    const [q, setQ] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'read' | 'unread'>('all');

    // pagination (سرور ساپورتش داره، ولی اینجا هم می‌تونیم سمت کلاینت کنترل کنیم)
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);

    const loadData = async (opts?: { page?: number; limit?: number }) => {
        try {
            setError('');
            if (!opts) setLoading(true);
            const res: ListResponse = await clientNotificationsAPI.list({
                page: opts?.page || page,
                limit: opts?.limit || pageSize,
                only_unread: false,
            });

            if (!res || res.success === false) {
                throw new Error((res as any)?.message || 'خطا در دریافت اعلان‌ها');
            }

            setItems(res.items || []);
            setTotal(res.total || res.items?.length || 0);
        } catch (e: any) {
            setError(e?.message || 'خطای داخلی سرور در دریافت اعلان‌ها');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // وقتی سایز صفحه عوض می‌شه، از صفحه ۱ شروع کن
    useEffect(() => {
        loadData({ page: 1, limit: pageSize });
        setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageSize]);

    const handleRefresh = async () => {
        setRefreshing(true);
        await loadData({ page, limit: pageSize });
    };

    const handleMarkAllRead = async () => {
        try {
            const unreadIds = items.filter((n) => !n.is_read).map((n) => n.id);
            if (unreadIds.length === 0) return;

            await clientNotificationsAPI.markRead({ ids: unreadIds });

            // optimistic update
            setItems((prev) =>
                prev.map((n) => (unreadIds.includes(n.id) ? { ...n, is_read: true } : n)),
            );
        } catch (e) {
            console.error(e);
            // می‌تونی اینجا toast هم استفاده کنی اگر سونر را در این فایل داری
            alert('خطا در علامت‌زدن اعلان‌ها به‌عنوان خوانده شده');
        }
    };

    const filtered = useMemo(() => {
        const text = q.trim().toLowerCase();

        return items.filter((n) => {
            const title = (n.title || '').toLowerCase();
            const body = (n.body || n.message || '').toLowerCase();
            const category = (n.category || n.type || '').toLowerCase();

            const textMatch =
                !text ||
                title.includes(text) ||
                body.includes(text) ||
                category.includes(text);

            const statusMatch =
                statusFilter === 'all'
                    ? true
                    : statusFilter === 'unread'
                        ? !n.is_read
                        : !!n.is_read;

            return textMatch && statusMatch;
        });
    }, [items, q, statusFilter]);

    const totalItems = filtered.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [totalPages, page]);

    const pageSlice = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filtered.slice(start, start + pageSize);
    }, [filtered, page, pageSize]);

    const stats = useMemo(() => {
        const unread = items.filter((i) => !i.is_read).length;
        const read = items.length - unread;
        return { total: items.length, unread, read };
    }, [items]);

    /* ---------- Render ---------- */

    if (loading) {
        return (
            <div
                className="flex items-center justify-center h-screen bg-gradient-to-br from-emerald-50 via-emerald-50 to-teal-50"
                dir="rtl"
            >
                <div className="text-center">
                    <Loader2 className="h-12 w-12 animate-spin text-emerald-600 mx-auto mb-4" />
                    <p className="text-emerald-700 font-medium">در حال بارگذاری اعلان‌ها...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6" dir="rtl">
                <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
                <div className="mt-3">
                    <Button
                        variant="outline"
                        onClick={handleRefresh}
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    >
                        <RefreshCw className="ml-2 h-4 w-4" />
                        تلاش مجدد
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div
            className="min-h-screen space-y-6 p-4 md:p-8 bg-gradient-to-br from-emerald-50 via-emerald-50 to-teal-50"
            dir="rtl"
        >
            {/* Header */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-600 via-emerald-600 to-teal-600 p-8 shadow-2xl">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-15" />
                <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="text-white space-y-2">
                        <div className="flex items-center gap-3">
                            <Sparkles className="h-8 w-8" />
                            <h1 className="text-3xl md:text-4xl font-bold flex items-center gap-2">
                                <BellRing className="h-8 w-8" />
                                اعلان‌ها
                            </h1>
                        </div>
                        <p className="text-emerald-100 text-lg">
                            پیام‌ها، به‌روزرسانی‌ها و رویدادهای مرتبط با سفارش‌ها، فاکتورها و لایسنس‌ها در این بخش
                            نمایش داده می‌شوند.
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-3">
                        <div className="bg-white/15 backdrop-blur-sm rounded-xl px-5 py-3 text-white border border-white/30 min-w-[120px]">
                            <div className="text-xs opacity-90">کل اعلان‌ها</div>
                            <div className="text-2xl font-bold">{fa.format(stats.total)}</div>
                        </div>
                        <div className="bg-white/15 backdrop-blur-sm rounded-xl px-5 py-3 text-white border border-white/30 min-w-[120px]">
                            <div className="text-xs opacity-90">خوانده نشده</div>
                            <div className="text-2xl font-bold text-amber-200">
                                {fa.format(stats.unread)}
                            </div>
                        </div>
                        <div className="bg-white/15 backdrop-blur-sm rounded-xl px-5 py-3 text-white border border-white/30 min-w-[120px]">
                            <div className="text-xs opacity-90">خوانده شده</div>
                            <div className="text-2xl font-bold text-emerald-200">
                                {fa.format(stats.read)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <Card className="border-emerald-200/60 shadow-lg bg-white/85 backdrop-blur-sm">
                <CardHeader className="py-4 bg-gradient-to-r from-emerald-50 to-emerald-50/70">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-xl bg-emerald-600 flex items-center justify-center shadow-sm">
                                <FilterIcon className="h-5 w-5 text-white" />
                            </div>
                            <div>
                                <CardTitle className="text-emerald-900 text-lg">فیلترها و جستجو</CardTitle>
                                <CardDescription className="text-emerald-700">
                                    جستجو میان اعلان‌ها و فیلتر بر اساس وضعیت خوانده‌شدن
                                </CardDescription>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <Button
                                variant="outline"
                                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                onClick={handleRefresh}
                                disabled={refreshing}
                            >
                                <RefreshCw
                                    className={`ml-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`}
                                />
                                به‌روزرسانی
                            </Button>

                            <Button
                                variant="outline"
                                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                onClick={handleMarkAllRead}
                            >
                                <CheckCircle2 className="ml-2 h-4 w-4" />
                                علامت‌زدن همه به‌عنوان خوانده شده
                            </Button>
                        </div>
                    </div>
                </CardHeader>

                <CardContent className="space-y-4 pt-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <div className="space-y-2 lg:col-span-2">
                            <label className="text-emerald-900 font-medium text-sm">جستجو</label>
                            <div className="relative">
                                <SearchIcon className="absolute left-3 top-3.5 h-5 w-5 text-emerald-300" />
                                <Input
                                    value={q}
                                    onChange={(e) => {
                                        setQ(e.target.value);
                                        setPage(1);
                                    }}
                                    placeholder="جستجو در عنوان، متن یا دسته‌بندی اعلان..."
                                    className="pl-11 h-11 border-emerald-200 focus:ring-emerald-500 focus:border-emerald-500 rounded-xl"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-emerald-900 font-medium text-sm">وضعیت</label>
                            <select
                                value={statusFilter}
                                onChange={(e) => {
                                    setStatusFilter(e.target.value as any);
                                    setPage(1);
                                }}
                                className="h-11 w-full rounded-xl border border-emerald-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            >
                                <option value="all">🔘 همه</option>
                                <option value="unread">📩 فقط خوانده نشده</option>
                                <option value="read">✅ فقط خوانده شده</option>
                            </select>
                        </div>
                    </div>

                    <div className="flex justify-between items-center gap-3 text-xs text-emerald-700 bg-emerald-50/60 border border-emerald-100 rounded-xl px-3 py-2">
                        <div className="flex items-center gap-2">
                            <Bell className="h-4 w-4" />
                            <span>
                {fa.format(totalItems)} اعلان مطابق فیلترهای فعلی نمایش داده می‌شود.
              </span>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-emerald-700 hover:bg-emerald-100"
                            onClick={() => {
                                setQ('');
                                setStatusFilter('all');
                                setPage(1);
                            }}
                        >
                            ریست فیلترها
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* List */}
            {items.length === 0 ? (
                <Card className="shadow-lg border-emerald-100 bg-white/80 backdrop-blur-sm">
                    <CardContent className="text-center py-16">
                        <Bell className="h-16 w-16 mx-auto mb-4 text-emerald-200" />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            هنوز هیچ اعلانی برای شما ثبت نشده است
                        </h3>
                        <p className="text-gray-500">
                            وقتی سفارشی ثبت کنید یا تغییری در حساب شما ایجاد شود، اعلان‌های مربوطه
                            در این بخش ظاهر می‌شود.
                        </p>
                    </CardContent>
                </Card>
            ) : filtered.length === 0 ? (
                <Card className="shadow-lg border-emerald-100 bg-white/80 backdrop-blur-sm">
                    <CardContent className="text-center py-12">
                        <XCircle className="h-12 w-12 mx-auto mb-3 text-amber-300" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            موردی با این فیلترها پیدا نشد
                        </h3>
                        <p className="text-gray-500 mb-3">فیلترها یا متن جستجو را تغییر دهید.</p>
                        <Button
                            variant="outline"
                            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            onClick={() => {
                                setQ('');
                                setStatusFilter('all');
                                setPage(1);
                            }}
                        >
                            ریست فیلترها
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-4">
                    {pageSlice.map((n) => {
                        const title =
                            n.title ||
                            (n.category ? `اعلان ${n.category}` : 'اعلان سیستمی');
                        const body = n.body || n.message || '';
                        const category = n.category || n.type || '';

                        return (
                            <div
                                key={n.id}
                                className={`relative overflow-hidden rounded-2xl border bg-gradient-to-br from-emerald-50/70 to-white shadow-md hover:shadow-xl transition-all duration-300 ${statusColor(
                                    n.is_read,
                                )}`}
                            >
                                <div className="flex flex-col md:flex-row md:items-stretch">
                                    {/* main */}
                                    <div className="flex-1 p-4 md:p-5 flex flex-col justify-between">
                                        <div className="flex items-start gap-3">
                                            <div className="mt-1">
                                                {n.is_read ? (
                                                    <Bell className="h-5 w-5 text-gray-400" />
                                                ) : (
                                                    <BellRing className="h-5 w-5 text-emerald-500" />
                                                )}
                                            </div>
                                            <div className="space-y-1">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <h3 className="text-sm md:text-base font-semibold text-gray-900">
                                                        {markMatch(title, q.trim())}
                                                    </h3>
                                                    {category && (
                                                        <Badge
                                                            variant="outline"
                                                            className="border-emerald-300 bg-emerald-50 text-emerald-800 text-[11px]"
                                                        >
                                                            {category}
                                                        </Badge>
                                                    )}
                                                    <span
                                                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] border ${statusColor(
                                                            n.is_read,
                                                        )}`}
                                                    >
                            {n.is_read ? (
                                <CheckCircle2 className="h-3.5 w-3.5 ml-1" />
                            ) : (
                                <Clock className="h-3.5 w-3.5 ml-1" />
                            )}
                                                        {statusLabel(n.is_read)}
                          </span>
                                                </div>
                                                {body && (
                                                    <p className="text-xs md:text-sm text-gray-700 whitespace-pre-line mt-1">
                                                        {markMatch(body, q.trim())}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[11px] text-gray-500">
                                            <span>تاریخ: {faDateTime(n.created_at)}</span>
                                            {n.link_url && (
                                                <a
                                                    href={n.link_url}
                                                    className="text-emerald-700 hover:text-emerald-900 hover:underline"
                                                >
                                                    مشاهده مرتبط
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* pagination */}
                    <Card className="border-emerald-200/60 shadow-lg bg-white/85 backdrop-blur-sm">
                        <CardContent className="py-4">
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <span className="text-sm text-gray-600 font-medium">نمایش:</span>
                                    <select
                                        value={pageSize}
                                        onChange={(e) => {
                                            setPageSize(parseInt(e.target.value, 10));
                                        }}
                                        className="h-10 rounded-xl border border-emerald-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                    >
                                        <option value={5}>۵ مورد</option>
                                        <option value={10}>۱۰ مورد</option>
                                        <option value={20}>۲۰ مورد</option>
                                        <option value={50}>۵۰ مورد</option>
                                    </select>
                                </div>

                                <div className="text-sm text-gray-700 text-center font-medium bg-emerald-50 px-4 py-2 rounded-xl border border-emerald-100">
                                    صفحه{' '}
                                    <span className="text-emerald-700 font-bold">
                    {fa.format(page)}
                  </span>{' '}
                                    از{' '}
                                    <span className="text-emerald-700 font-bold">
                    {fa.format(totalPages)}
                  </span>
                                    {' — '}
                                    <span className="text-emerald-700 font-bold">
                    {fa.format(totalItems)}
                  </span>{' '}
                                    اعلان
                                </div>

                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        disabled={page <= 1}
                                        className="flex-1 sm:flex-none border-emerald-400 text-emerald-700 hover:bg-emerald-100 disabled:opacity-40 transition-all rounded-xl"
                                    >
                                        <ChevronRight className="ml-1 h-4 w-4" />
                                        قبلی
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                            setPage((p) => Math.min(totalPages, p + 1))
                                        }
                                        disabled={page >= totalPages}
                                        className="flex-1 sm:flex-none border-emerald-400 text-emerald-700 hover:bg-emerald-100 disabled:opacity-40 transition-all rounded-xl"
                                    >
                                        بعدی
                                        <ChevronLeft className="mr-1 h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default NotificationsPage;
